import React, { useEffect, useState } from 'react';
import axiosLibrary from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';
import { useHistory } from 'react-router-dom';
import { client } from '@whizz/api-client';
import { useI18n } from '@whizz/react-i18n';
import { Alert } from '../../../components';
import { GLOBAL_ICONS, ROUTES } from '../../../util/vars';
import { StatusCodes } from 'http-status-codes';
import { useModal } from '../../../hooks/useModal';
import { CancelButtonModal, ConfirmButtonModal } from '../../../global';
import { ModalFooter } from '../../../components/modal/Modal';

type ErrorState = 'timeout' | 'server' | 'unauthorized' | 'generic' | null;

const axios = client.http();
// This config would impact on any dependency using this instance from `api-client`. Could potentially collide with requests
// made on `react-identity` et al.
axiosRetry(axios, { retryDelay: exponentialDelay });

const ErrorMessage = (): JSX.Element => {

  const { translate } = useI18n();
  const history = useHistory();
  const [ apiError, setApiError ] = useState<ErrorState>(null);

  const close = () => setApiError(null);
  const logout = () => history.push(ROUTES.logout);

  const [ modal, toggle, setProps ] = useModal(() => (
    <>
      <div className={'container'}>
        <Alert show type={'error'} className={'mb-lg'}>
          {translate('global.API_ERROR')}
        </Alert>
      </div>
      <ModalFooter>
        <CancelButtonModal onClick={close}>
          {translate('components.BUTTONS.OK')}
        </CancelButtonModal>
        <ConfirmButtonModal
          icon={GLOBAL_ICONS.logout}
          onClick={logout}
        >
          {translate('components.BUTTONS.LOGOUT')}
        </ConfirmButtonModal>
      </ModalFooter>
    </>
  ), {
    alwaysOnTop: true,
  });

  useEffect(() => {
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axiosLibrary.isCancel(error)) {
          return Promise.reject(error);
        }
        let err: ErrorState = null;
        if (!error.response) {
          err = 'generic';
        } else if (error.response.status >= 400 && error.response.status <= 599 ) {
          //TODO: Display different message depending on status
          const { status } = error.response;
          switch (status) {
            case StatusCodes.BAD_GATEWAY:
            case StatusCodes.GATEWAY_TIMEOUT:
              err = 'timeout';
              break;
            case StatusCodes.INTERNAL_SERVER_ERROR:
            case StatusCodes.SERVICE_UNAVAILABLE:
              err = 'server';
              break;
            case StatusCodes.UNAUTHORIZED:
              err = 'unauthorized';
              break;
            default:
              err = 'generic';
              break;
          }
        }

        setApiError(err);
        return Promise.reject(error);
      },
    );
  }, []);

  useEffect(() => {
    if (apiError) {
      toggle(true);
      setProps({
        size: 'sm',
        title: translate('global.ERROR'),
      });
    } else {
      toggle(false);
    }
  }, [ apiError ]);

  return modal;
};

ErrorMessage.displayName = 'ErrorMessage';

export default ErrorMessage;
